import { For, Show, createSignal } from 'solid-js';
import Text from '../Text';
import Modal from '../Modal';
import { EmptyState } from '../EmptyState';
import { InlineEdit } from '../InlineEdit';
import { useTranslation } from '../../hooks/useTranslation';
import { formatDateTime } from '../../services/dateFormat';
import { enabledCount, type BlockPreset } from '../../mapart/presets';
import styles from './PresetDialog.module.css';

// Modal + EmptyState do the shell; the rows are local because no shared list component carries
// per-row apply/delete actions with a save form on top.
interface Props {
    open: boolean;
    presets: BlockPreset[];
    colourCount: number;
    currentCount: number;
    onClose: () => void;
    onApply: (preset: BlockPreset) => void;
    onSave: (name: string) => void;
    onUpdate: (preset: BlockPreset) => void;
    onRename: (preset: BlockPreset, name: string) => void;
    onDelete: (preset: BlockPreset) => void;
    onExport: () => void;
    onImport: () => void;
}

export default function PresetDialog(props: Props) {
    const { t } = useTranslation();
    const [name, setName] = createSignal('');

    const save = () => {
        const trimmed = name().trim();
        if (!trimmed) return;
        props.onSave(trimmed);
        setName('');
    };

    return (
        <Show when={props.open}>
            <Modal open={true} onClose={props.onClose} maxWidth="34rem" title={<Text val="MAPART_PRESETS_TITLE" />}>
                <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                    <div class={styles.current}>
                        <i class="fa-solid fa-palette" />
                        <span>{t('MAPART_PRESETS_CURRENT', props.currentCount, props.colourCount)}</span>
                    </div>

                    <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.groupLabel}><Text val="MAPART_PRESETS_OWN" /></span>
                            <span class={styles.meta}><Text val="MAPART_PRESETS_EDIT_HINT" /></span>
                        </div>

                        <Show when={props.presets.length > 0} fallback={
                            <EmptyState
                                illustration="generic"
                                title={t('MAPART_PRESETS_EMPTY')}
                                hint={t('MAPART_PRESETS_EMPTY_HINT')}
                            />
                        }>
                            <div class={styles.list}>
                                <For each={props.presets}>{(preset) => (
                                    <div class={styles.row}>
                                        <div class={styles.info}>
                                            <InlineEdit
                                                value={preset.name}
                                                ariaLabel={t('MAPART_PRESETS_RENAME')}
                                                validate={(next: string) => (next.trim() ? null : t('MAPART_PRESETS_NAME_REQUIRED'))}
                                                onSave={(next: string) => props.onRename(preset, next.trim())}
                                            />
                                            <span class={styles.meta}>
                                                {t('MAPART_PRESETS_COLOURS', enabledCount(preset.selection), props.colourCount)}
                                                {' · '}
                                                {formatDateTime(preset.updatedAt)}
                                            </span>
                                        </div>
                                        <div class={styles.actions}>
                                            <button type="button" class="btn btn-secondary btn-sm" onClick={() => { props.onApply(preset); props.onClose(); }}>
                                                <i class="fa-solid fa-arrow-right-to-bracket" /> <Text val="MAPART_PRESETS_APPLY" />
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-secondary btn-sm"
                                                title={t('MAPART_PRESETS_UPDATE_HINT')}
                                                onClick={() => props.onUpdate(preset)}
                                            >
                                                <i class="fa-solid fa-floppy-disk" /> <Text val="MAPART_PRESETS_UPDATE" />
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-danger btn-sm btn-icon"
                                                title={t('DELETE')}
                                                onClick={() => props.onDelete(preset)}
                                            >
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>
                                )}</For>
                            </div>
                        </Show>
                    </div>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="MAPART_PRESETS_SAVE_LABEL" /></span>
                        <div class={styles.saveRow}>
                            <input
                                type="text"
                                value={name()}
                                placeholder={t('MAPART_PRESETS_SAVE_PLACEHOLDER')}
                                onInput={(e) => setName(e.currentTarget.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
                            />
                            <button type="button" class="btn btn-primary" disabled={!name().trim()} onClick={save}>
                                <i class="fa-solid fa-floppy-disk" /> <Text val="MAPART_PRESETS_SAVE" />
                            </button>
                        </div>
                    </label>

                    <div class={styles.footer}>
                        <button type="button" class="btn btn-secondary" onClick={props.onImport}>
                            <i class="fa-solid fa-file-arrow-up" /> <Text val="MAPART_PRESETS_IMPORT" />
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            disabled={props.presets.length === 0}
                            onClick={props.onExport}
                        >
                            <i class="fa-solid fa-file-arrow-down" /> <Text val="MAPART_PRESETS_EXPORT" />
                        </button>
                    </div>
                </div>
            </Modal>
        </Show>
    );
}
